import classNames from 'classnames';
import { ComponentProps, ReactNode, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import { ModalAriaLabelledByContext } from '@alltrails/modal';
import Link from '@alltrails/denali/components/Link';
import Logo from '@alltrails/denali/components/Logo';
import { useExperiments } from '@alltrails/experiments';
import AltLoginsForm from '../AltLoginsForm';
import SectionDivider from '../SectionDivider';
import TermsOfService from '../TermsOfService';
import styles from './SignUpModal.module.scss';

type InitialProps = Pick<ComponentProps<typeof AltLoginsForm>, 'appleLoginHandler' | 'facebookLoginHandler' | 'googleLoginHandler'> & {
  heading?: ReactNode;
  onCreateAccountClick: () => void;
  onLogInClick: () => void;
};

// Desktop: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=251%3A16532
// Mobile: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=250%3A16053

const Initial = ({ heading, onCreateAccountClick, onLogInClick, ...loginHandlers }: InitialProps) => {
  const ariaLabelledBy = useContext(ModalAriaLabelledByContext);
  const experiments = useExperiments();
  const isConsolidatedAuth = experiments['web-growth-rm-consolidate-auth']?.value === 'treatment';

  return (
    <div className={styles.content}>
      <div className={styles.logo}>
        <Logo color="monochrome-dark" size="lg" variant="symbol" />
      </div>
      <Heading as="h4" className={classNames(styles.heading, { [styles.headingMaxWidth]: isConsolidatedAuth })} id={ariaLabelledBy} size="400">
        {heading || <FormattedMessage defaultMessage="To keep exploring trails, log in or sign up for free" />}
      </Heading>
      <AltLoginsForm {...loginHandlers} authType="signup" flatColors={isConsolidatedAuth} />
      {!isConsolidatedAuth && (
        <SectionDivider classes={styles.divider}>
          <FormattedMessage defaultMessage="or" />
        </SectionDivider>
      )}
      <div className={styles.signUpLogInContainer}>
        <Button
          className={isConsolidatedAuth ? styles.email : undefined}
          text={
            isConsolidatedAuth ? (
              <FormattedMessage defaultMessage="Continue with email" />
            ) : (
              <FormattedMessage defaultMessage="Create a free account" />
            )
          }
          onClick={onCreateAccountClick}
          testId="initial-sign-up-screen-create-account"
          variant={isConsolidatedAuth ? 'default' : 'primary'}
          width="full"
        />
      </div>
      {!isConsolidatedAuth && (
        <div className={styles.signUpLogInContainer}>
          <Typography className={styles.inline} color="secondary" component="div" variant="text200">
            <FormattedMessage defaultMessage="Already have an account?" />{' '}
          </Typography>
          <Link size="md" onClick={onLogInClick}>
            <FormattedMessage defaultMessage="Log in" />
          </Link>
        </div>
      )}
      <TermsOfService />
    </div>
  );
};

export default Initial;
